import { differenceInDays } from 'date-fns'
import { head, last, pick } from 'lodash-es'
import result from 'lodash-es/result'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateRange, DayPicker, DayPickerProps, Mode } from 'react-day-picker'
import 'react-day-picker/style.css'
import dayjs from 'dayjs'

import {
  DatePickerCalendarWrapper,
  DatePickerControlsWrapper,
  DatePickerTrigger,
  DatePickerTriggerSeparator,
} from '@/lib/core/components/DatePicker/DatePicker.styles'
import { SHORT_DATE_FORMAT } from '@/lib/core/constants/constants'

import styles from './DatePicker.module.css'

type DatePickerDate = Date | Array<Date> | DateRange
type DatePickerApiFormat =
  | string
  | Array<string>
  | {
      from: string | undefined
      to: string | undefined
    }

type DDP = Exclude<DayPickerProps, { mode?: undefined; required?: undefined }>

export type DatePickerProps = {
  children?: ReactNode
  customTrigger?: boolean
  hasError?: boolean
  hasErrorFrom?: boolean
  hasErrorTo?: boolean
  initialDate?: DDP['selected'] | DatePickerApiFormat
  isDateSearchOpen?: boolean
  placeholderFrom?: string
  placeholderTo?: string
  resetIconState?: () => void
  disabledRangeStart?: boolean
  onChange?: DDP['onSelect']
  postion?: 'top' | 'bottom'
  testId?: string
} & Omit<DDP, 'selected' | 'onSelect'>

export const parseInitialDate = (date: DatePickerProps['initialDate']): DatePickerDate => {
  if (typeof date === 'string') {
    return dayjs(date).toDate()
  }

  if (Array.isArray(date)) {
    return date.map((item) => dayjs(item).toDate())
  }

  if (date && 'from' in date && 'to' in date) {
    return {
      from: date.from ? new Date(date.from) : new Date(),
      to: date.to ? new Date(date.to) : undefined,
    } as DateRange
  }

  return dayjs().toDate()
}

export const DatePicker = ({
  children: _children,
  hasError,
  hasErrorFrom,
  hasErrorTo,
  customTrigger,
  initialDate,
  isDateSearchOpen = false,
  onChange,
  placeholderFrom,
  placeholderTo,
  resetIconState,
  disabledRangeStart = false,
  mode,
  postion = 'bottom',
  testId,
  ...props
}: DatePickerProps) => {
  const [selected, setSelected] = useState<
    Exclude<DatePickerProps['initialDate'], DatePickerApiFormat>
  >(initialDate ? parseInitialDate(initialDate) : undefined)

  const positionRef = useRef<HTMLDivElement>(null)
  const dialogElement = useRef<HTMLDialogElement>(null)

  const onDialogClosed = useCallback((e: Event) => {
    if ((e as ToggleEvent).newState === 'close') {
      resetIconState?.()
    }
  }, [])

  // This is workaround because react is bugged and not firing the event
  useEffect(() => {
    dialogElement.current?.addEventListener('toggle', onDialogClosed)
  }, [dialogElement.current])

  const openDialog = useCallback(() => {
    dialogElement.current?.showPopover()

    const dialogRect = dialogElement.current?.getBoundingClientRect()
    const refRect = positionRef.current?.getBoundingClientRect()
    if (refRect && dialogRect) {
      const top = refRect.y + (customTrigger ? 0 : refRect.height) - (postion === 'top' ? 336 : 0)
      const left = refRect.x - (customTrigger ? dialogRect.width : 0)
      dialogElement.current?.style.setProperty('top', `${top}px`)
      dialogElement.current?.style.setProperty('left', `${left}px`)
    }
  }, [])

  const isRangeMode = mode === 'range'
  const isMultipleMode = mode === 'multiple'
  const propsByMode = useMemo(() => {
    const propsDictionary: { [key in Mode]: Partial<DayPickerProps> } = {
      multiple: {
        mode: 'multiple',
        onSelect: (dates: Array<Date> | undefined) => {
          const sortedDates = dates
            ? [...dates].sort((curr, next) => dayjs(curr).unix() - dayjs(next).unix())
            : []

          setSelected(sortedDates)

          // @ts-expect-error Typescript is not able to infer the type even if the key is of the mode type
          onChange?.(sortedDates)
        },
      },
      range: {
        mode: 'range',
        numberOfMonths: 2,
        onSelect: (range: DateRange | undefined) => {
          if (disabledRangeStart) {
            // Not allow double-click to re-select the start date with active shares
            const parsedInitialDate = parseInitialDate(initialDate!) as DateRange
            if (
              !dayjs(parsedInitialDate.from).isSame(dayjs(range?.from)) &&
              dayjs(parsedInitialDate.from).isBefore(dayjs().endOf('day'))
            ) {
              return
            }
          }

          setSelected(range)

          // @ts-expect-error Typescript is not able to infer the type even if the key is of the mode type
          onChange?.(range)
        },
      },
      single: {
        mode: 'single',
        onSelect: (date: Date | undefined) => {
          setSelected(date)

          // @ts-expect-error Typescript is not able to infer the type even if the key is of the mode type
          onChange?.(date!)
        },
      },
    }

    return propsDictionary[mode || 'single']
  }, [mode])

  const displayValue = result(
    {
      single: () => ({
        from: selected as Date,
        to: undefined,
      }),
      range: () => pick(selected, ['from', 'to']),
      multiple: () => ({
        from: head(selected as Array<Date>),
        to: last(selected as Array<Date>),
      }),
    },
    mode || 'single',
    () => ({
      from: undefined,
      to: undefined,
    }),
  )

  useEffect(() => {
    if (isDateSearchOpen) dialogElement.current?.showPopover()
    else dialogElement.current?.hidePopover()
  }, [isDateSearchOpen])

  return (
    <>
      {customTrigger ? (
        <div ref={positionRef} />
      ) : (
        <DatePickerControlsWrapper ref={positionRef} data-testid={testId}>
          <DatePickerTrigger
            $hasError={hasError || hasErrorFrom}
            $hasValue={!!displayValue?.from}
            onClick={openDialog}
            type="button"
            disabled={typeof props.disabled == 'boolean' ? props.disabled : disabledRangeStart}
          >
            {displayValue?.from
              ? dayjs(displayValue.from).format(SHORT_DATE_FORMAT)
              : placeholderFrom}
          </DatePickerTrigger>
          {(isRangeMode || isMultipleMode) && (
            <>
              <DatePickerTriggerSeparator>TO</DatePickerTriggerSeparator>
              <DatePickerTrigger
                $hasError={hasError || hasErrorTo}
                $hasValue={!!displayValue?.to}
                onClick={openDialog}
                type="button"
                disabled={typeof props.disabled == 'boolean' ? props.disabled : undefined}
              >
                {displayValue?.to
                  ? dayjs(displayValue.to).format(SHORT_DATE_FORMAT)
                  : placeholderTo}
              </DatePickerTrigger>
            </>
          )}
        </DatePickerControlsWrapper>
      )}
      <dialog popover="auto" ref={dialogElement} className={styles.datePickerWrapper}>
        <DatePickerCalendarWrapper tabIndex={-1}>
          <DayPicker
            defaultMonth={displayValue.from}
            weekStartsOn={1}
            formatters={{
              formatWeekdayName: (day, options) =>
                options?.locale?.localize?.day(dayjs(day).day())[0] || '',
            }}
            /* @ts-expect-error Day picker has dynamic typing and typescript gets confused with the props type because of it */
            selected={selected}
            {...props}
            {...propsByMode}
            modifiers={{
              outOfRange: (d) => {
                if (mode === 'single' || selected instanceof Date) return false
                const relData = Array.isArray(selected) ? selected[0] : selected?.from
                if (!relData) return false

                if ('max' in props && props.max !== undefined) {
                  const max = props?.max as number
                  const dayDiff = differenceInDays(d, relData)

                  return Math.abs(dayDiff) > max
                }
                if ('min' in props && props.min !== undefined) {
                  const min = props?.min as number
                  const dayDiff = differenceInDays(d, relData)

                  return Math.abs(dayDiff) < min
                }
                return false
              },
            }}
            modifiersClassNames={{
              outOfRange: styles.outOfRange,
            }}
          />
        </DatePickerCalendarWrapper>
      </dialog>
    </>
  )
}
